/**
 * AccessCodesPanel — community directory + creation entry point.
 *
 * Phase 2 (Gate Codes) + 2026-05-18 architecture refactor: communities
 * are now first-class objects. The shared workspace owns directory reads
 * and creation; this panel owns only sorting and the creation draft.
 *
 * - One flat list, sortable by name or address count.
 * - Clicking a row selects it; selection is keyed by community id.
 * - The "no active code" pill renders when a community has zero active
 *   codes but exists in its own right (the bug class that drove the
 *   refactor — a community is no longer derived from its codes).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowUpDown, Plus, X, Loader2, AlertTriangle, KeyRound, DoorClosed } from 'lucide-react';
import CommunityWorkspaceStatus from './CommunityWorkspaceStatus';

// ── inline create form ────────────────────────────────────────────────────

function NewCommunityForm({ existingNames, onCancel, onCreated, workspace }) {
  const { registerDirty } = workspace;
  const [name, setName] = useState('');
  const [type, setType] = useState('gate');
  const [code, setCode] = useState('');
  const [instructions, setInstructions] = useState('');
  const busy = workspace.busy;
  const dirty = useRef(false);
  dirty.current = Boolean(name || code || instructions || type !== 'gate');
  useEffect(() => registerDirty('new-community', () => dirty.current), [registerDirty]);

  const trimmedName = name.trim();
  const trimmedCode = code.trim();
  const duplicate = !workspace.acceptedCommunityId && trimmedName && existingNames.some(
    (n) => n.localeCompare(trimmedName, undefined, { sensitivity: 'base' }) === 0,
  );
  // Code is now optional on community creation — a community can exist
  // without any codes (the whole point of the refactor). If the user
  // leaves the code field blank, we only create the community.
  const valid = trimmedName && !duplicate;

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid || busy) return;
    const result = await workspace.createCommunity({
      name: trimmedName, type, code: trimmedCode, instructions: instructions.trim() || undefined,
    });
    if (result.ok) onCreated(result.communityId);
  };

  return (
    <form
      onSubmit={submit}
      className="border border-primary/30 bg-primary/5 rounded-lg p-3 space-y-3 mb-3"
      data-testid="new-community-form"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
          New Community
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-stone-400 hover:text-stone-700 transition-colors disabled:opacity-40"
          data-testid="new-community-cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-stone-600">Community name</label>
        {workspace.acceptedCommunityId && <p className="text-xs text-stone-600">This community already exists. Only the optional code will be submitted.</p>}
        <input
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy || Boolean(workspace.acceptedCommunityId)}
          placeholder="e.g. Spanish Hills HOA"
          className="w-full text-sm border border-stone-300 rounded-md px-2.5 py-1.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50"
          data-testid="new-community-name"
        />
        {duplicate && (
          <p className="text-[11px] text-amber-700 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> A community with this name already exists.
          </p>
        )}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-2 items-end">
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-stone-600">Type</label>
          <div className="inline-flex rounded-md border border-stone-300 overflow-hidden" data-testid="new-community-type">
            <button
              type="button"
              onClick={() => setType('gate')}
              disabled={busy}
              className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition-colors ${
                type === 'gate' ? 'bg-amber-50 text-amber-700' : 'bg-white text-stone-500 hover:bg-stone-50'
              }`}
              data-testid="new-community-type-gate"
            >
              <KeyRound className="h-3 w-3" /> Gate
            </button>
            <button
              type="button"
              onClick={() => setType('door')}
              disabled={busy}
              className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center gap-1 border-l border-stone-300 transition-colors ${
                type === 'door' ? 'bg-blue-50 text-blue-700' : 'bg-white text-stone-500 hover:bg-stone-50'
              }`}
              data-testid="new-community-type-door"
            >
              <DoorClosed className="h-3 w-3" /> Door
            </button>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-stone-600">
            Code <span className="text-stone-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            placeholder="e.g. 1234"
            className="w-full text-sm font-mono border border-stone-300 rounded-md px-2.5 py-1.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50"
            data-testid="new-community-code"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-stone-600">
          Instructions <span className="text-stone-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          disabled={busy || !trimmedCode}
          rows={2}
          placeholder="e.g. dial 9 then 1234, buzz at the main gatehouse"
          className="w-full text-xs border border-stone-300 rounded-md px-2.5 py-1.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50 resize-none"
          data-testid="new-community-instructions"
        />
      </div>

      {workspace.message && (
        <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid="new-community-error">
          <AlertTriangle className="h-3 w-3" /> {workspace.message}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-2.5 py-1 text-xs font-medium rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!valid || busy}
          className="px-3 py-1 text-xs font-medium rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors inline-flex items-center gap-1"
          data-testid="new-community-submit"
        >
          {busy ? (
            <><Loader2 className="h-3 w-3 animate-spin" /> Creating…</>
          ) : (
            <><Plus className="h-3 w-3" /> {workspace.acceptedCommunityId ? 'Save optional code' : 'Create community'}</>
          )}
        </button>
      </div>
    </form>
  );
}

// ── main component ────────────────────────────────────────────────────────

export default function AccessCodesPanel({ workspace }) {
  const { communities, listLoading: loading, listError: error, selectedCommunityId } = workspace;
  // 'name-asc' | 'name-desc' | 'count-asc' | 'count-desc'
  const [sortKey, setSortKey] = useState('name-asc');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (workspace.completedOperation?.kind === 'create-community') setCreating(false);
  }, [workspace.completedOperation]);
  useEffect(() => {
    if (workspace.status === 'retired') setCreating(false);
  }, [workspace.status]);

  const sorted = useMemo(() => {
    const [field, dir] = sortKey.split('-');
    const mult = dir === 'asc' ? 1 : -1;
    const out = [...communities];
    out.sort((a, b) => {
      if (field === 'name') {
        return mult * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      }
      if (a.address_count !== b.address_count) return mult * (a.address_count - b.address_count);
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    return out;
  }, [communities, sortKey]);

  const toggleSort = (field) => {
    setSortKey((prev) => {
      const [prevField, prevDir] = prev.split('-');
      if (prevField !== field) return `${field}-asc`;
      return `${field}-${prevDir === 'asc' ? 'desc' : 'asc'}`;
    });
  };

  const renderSortHint = (field, ascLabel, descLabel) => {
    const [activeField, activeDir] = sortKey.split('-');
    if (activeField !== field) return null;
    return (
      <span className="text-[10px] font-normal text-stone-400 normal-case">
        ({activeDir === 'asc' ? ascLabel : descLabel})
      </span>
    );
  };

  const handleCreated = () => setCreating(false);

  const renderHeader = () => (
    <div className="flex items-start justify-between gap-3 mb-4">
      {!creating && (
        <button
          type="button"
          onClick={() => { if (workspace.selectCommunity(null)) setCreating(true); }}
          disabled={workspace.readOnly}
          className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
          data-testid="new-community-trigger"
        >
          <Plus className="h-3.5 w-3.5" /> New Community
        </button>
      )}
    </div>
  );



  return (
    <div data-testid="access-codes-panel">
      <CommunityWorkspaceStatus workspace={workspace} />
      {loading && <p role="status" className="text-sm text-stone-500">Loading communities…</p>}
      {error && <div role="alert" className="text-sm text-red-600">{error} <button type="button" disabled={workspace.busy} onClick={() => workspace.refresh()}>Retry loading communities</button></div>}
      {!loading && !error && renderHeader()}

      {creating && workspace.status !== 'retired' && (
        <fieldset disabled={workspace.readOnly} className="min-w-0">
        <NewCommunityForm
          workspace={workspace}
          existingNames={communities.map((c) => c.name)}
          onCancel={() => { if (workspace.canLeave()) { workspace.resetSelection(); setCreating(false); } }}
          onCreated={handleCreated}
        />
        </fieldset>
      )}

      {communities.length === 0 && !loading && !error ? (
        <div
          className="text-sm text-stone-500 py-8 text-center border border-dashed border-stone-200 rounded-lg"
          data-testid="access-codes-empty"
        >
          {creating
            ? 'Fill out the form above to create your first community.'
            : 'No communities yet — click "New Community" to add one.'}
        </div>
      ) : (
        <div className="border border-stone-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] gap-4 px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
            <button
              type="button"
              onClick={() => toggleSort('name')}
              className="flex items-center gap-1.5 text-left hover:text-stone-700 transition-colors"
              data-testid="access-codes-sort-name"
            >
              Community
              <ArrowUpDown className="h-3 w-3" />
              {renderSortHint('name', 'A–Z', 'Z–A')}
            </button>
            <button
              type="button"
              onClick={() => toggleSort('count')}
              className="flex items-center gap-1.5 text-right justify-end hover:text-stone-700 transition-colors"
              data-testid="access-codes-sort-count"
            >
              Addresses
              <ArrowUpDown className="h-3 w-3" />
              {renderSortHint('count', 'low → high', 'high → low')}
            </button>
          </div>

          <ul className="divide-y divide-stone-100">
            {sorted.map((c) => {
              const isSelected = selectedCommunityId === c.id;
              return (
                <li
                  key={c.id}
                  onClick={() => { if (workspace.selectCommunity(isSelected ? null : c.id)) setCreating(false); }}
                  className={[
                    'grid grid-cols-[1fr_auto] gap-4 px-4 py-3 items-center cursor-pointer transition-colors',
                    isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-stone-50',
                  ].join(' ')}
                  data-testid={`access-codes-community-row-${c.id}`}
                >
                  <span className={`truncate text-sm font-medium ${isSelected ? 'text-primary' : 'text-stone-900'}`}>
                    {c.name}
                  </span>
                  <span className={`text-xs text-right flex items-center gap-2 justify-end ${isSelected ? 'text-primary/70' : 'text-stone-500'}`}>
                    {c.active_code_count === 0 && (
                      <span
                        className="text-[10px] font-medium uppercase tracking-wider text-stone-500 bg-stone-100 border border-stone-200 px-1.5 py-0.5 rounded"
                        data-testid={`access-codes-no-active-pill-${c.id}`}
                      >
                        no active code
                      </span>
                    )}
                    <span>
                      {c.address_count} {c.address_count === 1 ? 'address' : 'addresses'}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
